import { defineStore } from 'pinia'
import type {
    NetworkDataFromDetail,
    NetworkDataFromListing,
    Statistics,
    WarningMessage
} from '~/interfaces/network'
import { sortWithNull } from '#imports'
import useLongLocale from '~/composables/useLongLocale'
import useTokenStore from '~/store/token'

const useNetworkStore = defineStore('network', {
    state: () => ({
        slug: '',
        networkData: null as NetworkDataFromDetail | null,
        networkStatistics: null as Statistics | null,
        topTenNetworks: [] as Statistics[],
        rankedNetworks: [] as NetworkDataFromListing[],
        cachedDataNetworkDetailDate: '',
        cachedDataNetworkStatisticsDate: '',
        cachedDataNetworkRankedNetworksDate: '',
        cachedDataNetworkTopTenNetworksDate: ''
    }),
    getters: {
        id(state) {
            return state.networkData?.id || 0
        },
        name(state) {
            return state.networkData?.name || ''
        },
        chargingZonesNumber(state) {
            return state.networkData?.totalPoolsCount || 0
        },
        compatiblePrefix(state) {
            return state.networkData?.isChargemapPassCompatible
                ? 'compatible'
                : 'not_compatible'
        },
        isIonity(state) {
            return state.networkData?.name.toLowerCase() === 'ionity'
        },
        isTesla(state) {
            return state.networkData?.name.toLowerCase().includes('tesla')
        },
        globalNote(state) {
            return state.networkStatistics?.globalNoteAverage || 0
        },
        ratingsCount(state) {
            return state.networkStatistics?.ratingsCount || 0
        },
        materialNote(state) {
            return state.networkStatistics?.materialNoteAverage || 0
        },
        priceNote(state) {
            return state.networkStatistics?.priceNoteAverage || 0
        },
        locationNote(state) {
            return state.networkStatistics?.locationNoteAverage || 0
        },
        securityNote(state) {
            return state.networkStatistics?.securityNoteAverage || 0
        },
        checkinsCount(state) {
            return state.networkStatistics?.checkinsCount || 0
        },
        isCompatible(state) {
            return state.networkData?.isChargemapPassCompatible || false
        },
        warningMessage(state) {
            const localisedMessage = state.networkData?.warningMessages?.find(
                (message: WarningMessage) =>
                    message.i18nLocale === useLongLocale('_')
            )

            return localisedMessage?.message || ''
        },
        defaultCenter(state): [number, number] {
            return [
                state.networkData?.initialCenterLatitude || 0,
                state.networkData?.initialCenterLongitude || 0
            ]
        }
    },
    actions: {
        getRatingFromCommunityFeedbacks(network: NetworkDataFromListing) {
            const networkFoundInTopTen = this.topTenNetworks?.find(
                (networkInTopTen: Statistics) =>
                    network.id === networkInTopTen.networkId
            )

            return (
                networkFoundInTopTen?.globalNoteAverage ?? network.rating ?? 0
            )
        },
        async fetchNetworkData() {
            if (this.slug) {
                try {
                    ;[this.networkData, this.cachedDataNetworkDetailDate] =
                        await $fetch(
                            `${useRuntimeConfig().public.APPLICATION_URL}/api/networks/detail`,
                            {
                                query: {
                                    slug: this.slug,
                                    token: useTokenStore().token
                                }
                            }
                        )
                } catch (error: any) {
                    if (error.status !== 404) {
                        useNuxtApp().$throwError(error, 'FetchNetworkData')
                    }

                    return showError({
                        statusCode: error.status,
                        statusMessage: error.statusText,
                        fatal: true
                    })
                }
            }
        },
        async fetchRankedNetworks() {
            if (this.topTenNetworks.length) {
                const networkIds = this.topTenNetworks?.map(
                    (stat: Statistics) => stat.networkId
                )

                try {
                    ;[
                        this.rankedNetworks,
                        this.cachedDataNetworkRankedNetworksDate
                    ] = await $fetch(
                        `${useRuntimeConfig().public.APPLICATION_URL}/api/networks/rankedNetworks`,
                        {
                            query: {
                                networkIds,
                                token: useTokenStore().token
                            }
                        }
                    )
                    this.rankedNetworks.sort(
                        (
                            network1: NetworkDataFromListing,
                            network2: NetworkDataFromListing
                        ) =>
                            sortWithNull(
                                this.getRatingFromCommunityFeedbacks(network1),
                                this.getRatingFromCommunityFeedbacks(network2),
                                false
                            )
                    )
                } catch (error: any) {
                    if (error.status !== 404) {
                        useNuxtApp().$throwError(error, 'FetchRankedNetworks')
                    }

                    return showError({
                        statusCode: error.status,
                        statusMessage: error.statusText,
                        fatal: true
                    })
                }
            }
        },

        async fetchTopTenNetworks() {
            try {
                ;[
                    this.topTenNetworks,
                    this.cachedDataNetworkTopTenNetworksDate
                ] = await $fetch(
                    `${useRuntimeConfig().public.APPLICATION_URL}/api/networks/topTenNetworks`,
                    {
                        query: {
                            token: useTokenStore().token
                        }
                    }
                )
            } catch (error: any) {
                if (error.status !== 404) {
                    useNuxtApp().$throwError(error, 'FetchTopTenNetworks')
                }

                return showError({
                    statusCode: error.status,
                    statusMessage: error.statusText,
                    fatal: true
                })
            }
        },
        async fetchNetworkStatistics() {
            if (this.id) {
                try {
                    ;[
                        this.networkStatistics,
                        this.cachedDataNetworkStatisticsDate
                    ] = await $fetch(
                        `${useRuntimeConfig().public.APPLICATION_URL}/api/networks/statistics`,
                        {
                            query: {
                                id: this.id,
                                token: useTokenStore().token
                            }
                        }
                    )
                } catch (error: any) {
                    if (error.status !== 404) {
                        useNuxtApp().$throwError(
                            error,
                            'FetchNetworkStatistics'
                        )
                    }

                    return showError({
                        statusCode: error.status,
                        statusMessage: error.statusText,
                        fatal: true
                    })
                }
            }
        }
    }
})

type NetworkStore = Omit<
    ReturnType<typeof useNetworkStore>,
    keyof ReturnType<typeof defineStore>
>

export default useNetworkStore

export type { NetworkStore }
